import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { MapPin, Check } from 'lucide-react';
import { toast } from 'sonner';
import type { Ingredient } from '@/types/ingredient.types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

interface Location {
  id: number;
  nombre: string;
  direccion: string;
  estado: string;
  tipoLocal: string;
}

interface LocationsModalProps {
  ingredient: Ingredient | null;
  isOpen: boolean;
  onClose: () => void;
}

export const LocationsModal: React.FC<LocationsModalProps> = ({
  ingredient,
  isOpen,
  onClose
}) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<Set<number>>(new Set());
  const [initialLocations, setInitialLocations] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Keep track of the last valid ingredient to display during close animation
  const lastIngredientRef = useRef<Ingredient | null>(null);

  // Update the ref when we have a valid ingredient
  useEffect(() => {
    if (ingredient) {
      lastIngredientRef.current = ingredient;
    }
  }, [ingredient]);

  // Use last valid ingredient for display
  const displayIngredient = ingredient || lastIngredientRef.current;

  // Reset state when modal closes
  const resetState = useCallback(() => {
    setSelectedLocations(new Set());
    setInitialLocations(new Set());
    setLocations([]);
    setLoading(false);
    setSaving(false);
  }, []);

  useEffect(() => {
    if (isOpen && ingredient) {
      fetchData();
    } else if (!isOpen) {
      // Reset state after a delay to allow animation to complete
      const timer = setTimeout(() => {
        resetState();
        lastIngredientRef.current = null;
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, ingredient?.id, resetState]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');

      const locationsResponse = await fetch(`${API_BASE_URL}/locations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (locationsResponse.ok) {
        const locationsData = await locationsResponse.json();
        setLocations(locationsData.data || []);
      }

      if (ingredient?.id) {
        const assignedResponse = await fetch(`${API_BASE_URL}/ingredients/${ingredient.id}/locations`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (assignedResponse.ok) {
          const assignedData = await assignedResponse.json();
          const assignedIds = new Set<number>(
            (assignedData.data || []).map((ls: any) => ls.locationId)
          );
          setSelectedLocations(assignedIds);
          setInitialLocations(new Set(assignedIds));
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationToggle = (locationId: number) => {
    const newSelected = new Set(selectedLocations);
    if (newSelected.has(locationId)) {
      newSelected.delete(locationId);
    } else {
      newSelected.add(locationId);
    }
    setSelectedLocations(newSelected);
  };

  const hasChanges = () => {
    if (selectedLocations.size !== initialLocations.size) return true;
    for (const id of selectedLocations) {
      if (!initialLocations.has(id)) return true;
    }
    return false;
  };

  const handleSave = async () => {
    if (!ingredient?.id) return;

    try {
      setSaving(true);
      const token = localStorage.getItem('accessToken');

      const response = await fetch(`${API_BASE_URL}/ingredients/${ingredient.id}/locations`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          locationIds: Array.from(selectedLocations)
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast.success(result.message || 'Asignaciones guardadas exitosamente');
        onClose();
      } else {
        toast.error(result.error || 'Error al guardar las asignaciones');
      }
    } catch (error) {
      console.error('Error saving location assignments:', error);
      toast.error('Error al guardar las asignaciones');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !saving) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl"
        onPointerDownOutside={(e) => {
          // Prevent closing when clicking outside during save
          if (saving) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (saving) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-macartur-primary" />
            Asignar Locales - {displayIngredient?.name || ''}
          </DialogTitle>
        </DialogHeader>

        <div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-macartur-primary mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Cargando locales...</p>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Selecciona los locales donde este ingrediente estará disponible
              </p>

              <div className="space-y-2 max-h-80 overflow-y-auto">
                {locations.map((location) => {
                  const isSelected = selectedLocations.has(location.id);
                  const isActive = location.estado === 'ACTIVO';

                  return (
                    <div
                      key={location.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                        isSelected
                          ? 'border-macartur-primary bg-rose-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      } ${!isActive ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={() => isActive && handleLocationToggle(location.id)}
                    >
                      <div
                        className={`w-5 h-5 border-2 rounded flex items-center justify-center flex-shrink-0 ${
                          isSelected
                            ? 'border-macartur-primary bg-macartur-primary'
                            : 'border-gray-300'
                        }`}
                      >
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 truncate">
                            {location.nombre}
                          </span>
                          <span
                            className={`px-2 py-0.5 text-xs rounded-full flex-shrink-0 ${
                              isActive
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {isActive ? 'ACTIVO' : 'INACTIVO'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 truncate">{location.direccion}</p>
                        <p className="text-xs text-gray-400 capitalize">
                          {location.tipoLocal?.toLowerCase() || 'Sucursal'}
                        </p>
                      </div>
                    </div>
                  );
                })}

                {locations.length === 0 && !loading && (
                  <div className="text-center py-8">
                    <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No hay locales disponibles</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex justify-between items-center border-t pt-4 mt-4">
          <span className="text-sm text-gray-600">
            {selectedLocations.size} local(es) seleccionado(s)
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || loading || !hasChanges()}
              className="bg-macartur-primary hover:bg-macartur-primary/90"
            >
              {saving ? 'Guardando...' : 'Guardar Asignaciones'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
